import { useEffect, useState, type ReactNode } from 'react';
import type { ConVersion } from '../../api/cliente';
import { ErrorApi } from '../../api/problema';
import type { PreguntaServicio, ServicioCatalogo, VersionServicio } from '../../api/ops/servicios';
import { Ayuda } from '../../componentes/Ayuda';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Boton } from '../../componentes/Boton';
import { Campo } from '../../componentes/Campo';
import { Desplegable } from '../../componentes/Desplegable';
import { Icono } from '../../componentes/Icono';
import type { NombreIcono } from '../../componentes/iconos';
import { Interruptor } from '../../componentes/Interruptor';
import { PanelLateral } from '../../componentes/PanelLateral';
import { dinero } from '../../logica/dinero';
import { diaMes } from '../../logica/fechasCortas';
import type { PropsPantalla } from '../tipos';
import { textoDeError } from '../agenda/logica';
import type { DatosServicios } from './datos';
import {
    COLORES,
    DURACIONES,
    TIPOS_PREGUNTA,
    camposPregunta,
    camposServicio,
    claveNueva,
    ejemploUnidades,
    erroresPregunta,
    erroresServicio,
    estadoServicio,
    formDe,
    formPregunta,
    formVacio,
    iconoCategoria,
    lineasPregunta,
    nombreColor,
    reglaDelServicio,
    sinErroresPregunta,
    textoDuracion,
    unir,
    type ErroresServicio,
    type FormPregunta,
    type FormServicio,
} from './logica';
import { AYUDA, PESTANAS, SOLO_LECTURA, SOLO_VER, type PestanaServicio } from './textos';

/**
 * Ficha del servicio (9.4) en RV Panel. Nuevo: nombre, duración, precio y quién lo hace (todos los activos marcados, G436),
 * y tres desplegables plegados con su resumen (Para el cliente · Cuándo se puede pedir cita · Cómo se pide). Abierto, su
 * botonera Datos · Preguntas · Quién lo hace · Versiones (`?servicio=&tab=`). Cada cambio crea una versión nueva y pide la
 * leída (`If-Match`); la regla de reserva (Disponibilidad) y la política de cobro (Pagos) se ven aquí en solo lectura y
 * se cambian en su pantalla.
 */
interface Props extends PropsPantalla {
    datos: DatosServicios;
    servicio: ServicioCatalogo | null;
    pestana: PestanaServicio;
    gestiona: boolean;
    onPestana: (p: PestanaServicio) => void;
    onCerrar: () => void;
    onCreado: (s: ServicioCatalogo) => void;
    onCambiado: (s: ServicioCatalogo) => void;
}

export function FichaServicio(props: Props) {
    const { ops, negocio, movil, soloLectura, datos, servicio, pestana, gestiona, onPestana, onCerrar, onCreado, onCambiado } = props;
    const avisar = useAvisoBreve();
    const nuevo = servicio === null;
    const [version, setVersion] = useState<string | null>(null);
    const [form, setForm] = useState<FormServicio>(() => (servicio === null ? formVacio(datos.profesionales) : formDe(servicio)));
    const [quien, setQuien] = useState<string[]>(() => servicio?.professional_ids ?? []);
    const [probado, setProbado] = useState(false);
    const [ocupado, setOcupado] = useState(false);

    useEffect(() => {
        if (servicio === null) {
            return;
        }
        let vivo = true;
        ops.getService(negocio, servicio.id)
            .then((r) => {
                if (vivo) {
                    setVersion(r.version);
                    onCambiado(r.datos);
                }
            })
            .catch(() => undefined);
        return () => {
            vivo = false;
        };
        // Solo al abrir: después, la versión la da cada respuesta.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ops, negocio, servicio?.id]);

    const aplicar = (r: ConVersion<ServicioCatalogo>, texto: string) => {
        setVersion(r.version);
        onCambiado(r.datos);
        avisar({ tipo: 'ok', texto });
    };
    const fallo = (e: unknown, reintentar?: () => void) => {
        const conflicto = e instanceof ErrorApi && e.estado === 412;
        avisar({ tipo: 'error', texto: conflicto ? 'Alguien lo ha cambiado mientras tanto. Cierra la ficha y vuelve a abrirla.' : textoDeError(e), ...(reintentar === undefined ? {} : { reintentar }) });
    };

    const errores: ErroresServicio = probado ? erroresServicio(form) : {};
    const activa = servicio === null || servicio.status === 'active';
    const editable = gestiona && activa;

    const guardarDatos = () => {
        setProbado(true);
        if (Object.keys(erroresServicio(form)).length > 0) {
            return;
        }
        const campos = camposServicio(form, datos.moneda, datos.firma);
        setOcupado(true);
        if (servicio === null) {
            ops.createService(negocio, { ...campos, professional_ids: form.profesionales })
                .then((r) => {
                    avisar({ tipo: 'ok', texto: 'Servicio creado.' });
                    onCreado(r.datos);
                })
                .catch((e: unknown) => { fallo(e, guardarDatos); })
                .finally(() => { setOcupado(false); });
            return;
        }
        if (version === null) {
            setOcupado(false);
            return;
        }
        ops.updateService(negocio, servicio.id, campos, version)
            .then((r) => { aplicar(r, `Cambios guardados. Ya es la versión ${String(r.datos.current_version.number)}.`); })
            .catch((e: unknown) => { fallo(e); })
            .finally(() => { setOcupado(false); });
    };

    const guardarQuien = () => {
        if (servicio === null || version === null) {
            return;
        }
        setOcupado(true);
        ops.setServiceProfessionals(negocio, servicio.id, quien, version)
            .then((r) => { aplicar(r, 'Cambios guardados.'); })
            .catch((e: unknown) => { fallo(e); })
            .finally(() => { setOcupado(false); });
    };

    const archivar = (si: boolean) => {
        if (servicio === null || version === null) {
            return;
        }
        setOcupado(true);
        (si ? ops.archiveService(negocio, servicio.id, version) : ops.restoreService(negocio, servicio.id, version))
            .then((r) => { aplicar(r, si ? 'Servicio archivado. Sus citas siguen igual.' : 'El servicio se vuelve a ofrecer.'); })
            .catch((e: unknown) => { fallo(e); })
            .finally(() => { setOcupado(false); });
    };

    const v = servicio?.current_version;
    const estado = servicio === null ? null : estadoServicio(servicio);
    const accion = gestiona && (nuevo || (activa && (pestana === 'datos' || pestana === 'quien'))) ? (nuevo ? 'Crear servicio' : 'Guardar') : '';
    const ayuda = nuevo ? AYUDA.nuevo : AYUDA[pestana];

    const contenido = (() => {
        if (nuevo || pestana === 'datos') {
            return gestiona ? (
                <DatosEditables
                    {...props}
                    form={form}
                    setForm={setForm}
                    errores={errores}
                    editable={editable || nuevo}
                    onQuien={() => { onPestana('quien'); }}
                    pie={
                        servicio === null ? null : (
                            <div className="rv-sv-pie">
                                <span>{servicio.status === 'archived' ? `Archivado${servicio.archived_at == null ? '' : ` el ${diaMes(servicio.archived_at)}`}. Sus citas siguen igual.` : `Al guardar se crea la versión ${String((v?.number ?? 0) + 1)}. Las citas ya hechas siguen con la ${String(v?.number ?? 0)}.`}</span>
                                {servicio.status === 'active' ? (
                                    <Boton tipo="secundario" rojo icono="archive" movil={movil} disabled={ocupado || version === null} onClick={() => { archivar(true); }}>
                                        Archivar el servicio
                                    </Boton>
                                ) : (
                                    <Boton tipo="secundario" icono="archive-restore" movil={movil} disabled={ocupado || version === null} onClick={() => { archivar(false); }}>
                                        Volver a ofrecerlo
                                    </Boton>
                                )}
                            </div>
                        )
                    }
                />
            ) : (
                servicio !== null && <DatosLectura datos={datos} servicio={servicio} />
            );
        }
        if (pestana === 'preguntas') {
            return <Preguntas {...props} servicio={servicio} version={version} editable={editable} aplicar={aplicar} fallo={fallo} />;
        }
        if (pestana === 'quien') {
            const pros = [...datos.profesionales.filter((p) => p.status === 'active'), ...datos.profesionales.filter((p) => p.status !== 'active' && servicio.professional_ids.includes(p.id))];
            const nadie = !quien.some((id) => datos.profesionales.find((p) => p.id === id)?.status === 'active');
            return (
                <div className="rv-sv-lineas">
                    <p className="rv-sv-nota" style={{ paddingBottom: 8 }}>Solo se ofrecen huecos de quienes lo hacen. Si pidió «cualquiera», el reparto elige entre ellos.</p>
                    {pros.map((p) => (
                        <Interruptor
                            key={p.id}
                            etiqueta={p.display_name}
                            ayuda={p.status !== 'active' ? 'Desactivado: no recibe citas' : p.panel_access === 'none' ? 'Sin acceso al panel' : ''}
                            valor={quien.includes(p.id)}
                            desactivado={!editable || p.status !== 'active'}
                            onChange={(on) => { setQuien((q) => (on ? [...q, p.id] : q.filter((x) => x !== p.id))); }}
                        />
                    ))}
                    {nadie && (
                        <div className="rv-sv-alerta" role="alert" style={{ marginTop: 14 }}>
                            <Icono nombre="triangle-alert" />
                            <span>Sin nadie activo que lo haga, el servicio no se puede pedir en ningún canal.</span>
                        </div>
                    )}
                    <p className="rv-sv-nota" style={{ paddingTop: 14 }}>Las citas ya hechas no cambian de profesional.</p>
                </div>
            );
        }
        return <Versiones {...props} servicio={servicio} />;
    })();

    return (
        <PanelLateral
            abierto
            onCerrar={onCerrar}
            movil={movil}
            tamano={nuevo ? 'formulario' : 'ficha'}
            eyebrow={nuevo ? 'Nuevo servicio' : 'Servicio'}
            titulo={nuevo ? (form.nombre.trim() === '' ? 'Nuevo servicio' : form.nombre.trim()) : (v?.name ?? '')}
            subtitulo={nuevo || v === undefined ? 'Aparece en tu web, tu widget y el panel.' : [v.category ?? '', textoDuracion(v.base_duration_minutes), dinero(v.base_price), `versión ${String(v.number)}`].filter((x) => x !== '').join(' · ')}
            {...(estado !== null && estado.texto !== 'Activo' ? { chip: estado.texto, chipEstado: estado.chip } : {})}
            icono={iconoCategoria(nuevo ? form.categoria : v?.category)}
            accion={accion}
            accionIcono={nuevo ? 'plus' : 'check'}
            ocupado={ocupado}
            inactiva={!nuevo && version === null}
            onAccion={pestana === 'quien' && !nuevo ? guardarQuien : guardarDatos}
        >
            <div className={`rv-sv-ficha${nuevo ? ' rv-sv-ficha--sola' : ''}`}>
                {!nuevo && (
                    <div className="rv-sv-botonera" role="tablist" aria-label="Apartados del servicio">
                        {PESTANAS.map(([clave, texto, icono]) => (
                            <button key={clave} type="button" role="tab" aria-selected={pestana === clave} className="rv-sv-botonera__b" onClick={() => { onPestana(clave); }}>
                                <Icono nombre={icono} />
                                <span>{texto}</span>
                            </button>
                        ))}
                    </div>
                )}
                <div className="rv-sv-ficha__contenido">
                    {!nuevo && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <h3 className="rv-sv-ceja">{PESTANAS.find(([p]) => p === pestana)?.[1] ?? ''}</h3>
                            <span style={{ flex: 1 }} />
                            <Ayuda tarjetas={ayuda} />
                        </div>
                    )}
                    {!nuevo && !gestiona && (
                        <p className="rv-sv-nota">
                            <Icono nombre="eye" />
                            <span>{soloLectura ? SOLO_LECTURA : SOLO_VER}</span>
                        </p>
                    )}
                    {contenido}
                </div>
            </div>
        </PanelLateral>
    );
}

// ---- Datos ----

function DatosEditables({ datos, servicio, form, setForm, errores, editable, onQuien, pie, movil, navegar }: Props & { form: FormServicio; setForm: (f: (x: FormServicio) => FormServicio) => void; errores: ErroresServicio; editable: boolean; onQuien: () => void; pie: ReactNode }) {
    const [pliegue, setPliegue] = useState<'cliente' | 'cuando' | 'como' | null>(null);
    const nuevo = servicio === null;
    const pon = <K extends keyof FormServicio>(k: K) => (valor: FormServicio[K]) => { setForm((f) => ({ ...f, [k]: valor })); };
    const activos = datos.profesionales.filter((p) => p.status === 'active');
    const regla = reglaDelServicio(servicio?.id ?? null, datos.reglas);
    const politica = servicio?.policy_id == null ? null : (datos.politicas.find((p) => p.id === servicio.policy_id)?.name ?? 'Con política de cobro');
    const plantillas = datos.plantillas.filter((p) => p.status === 'active' || p.id === form.consentimiento);
    const quienTexto = (() => {
        const n = (servicio?.professional_ids ?? []).map((id) => datos.profesionales.find((p) => p.id === id)?.display_name).filter((x): x is string => x !== undefined);
        return n.length === 0 ? 'Nadie todavía: no se puede pedir.' : n.length > 3 ? `${n.slice(0, 2).join(', ')} y ${String(n.length - 2)} más` : unir(n);
    })();

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
            <fieldset disabled={!editable} style={{ border: 0, padding: 0, margin: 0 }} className="rv-sv-form">
                <Campo etiqueta="Nombre" valor={form.nombre} onChange={pon('nombre')} error={errores.nombre ?? null} autocompletar="off" mayusculas="sentences" max={120} nombre="servicio-nombre" />
                <div role="group" aria-label="Duración" className="rv-sv-grupo">
                    <span className="rv-sv-grupo__t">Duración</span>
                    <div className="rv-sv-pildoras">
                        {DURACIONES.map((d) => (
                            <button key={d.minutos} type="button" className="rv-sv-pq" aria-pressed={!form.otra && form.minutos === d.minutos} onClick={() => { setForm((f) => ({ ...f, minutos: d.minutos, otra: false })); }}>
                                {d.texto}
                            </button>
                        ))}
                        <button type="button" className="rv-sv-pq" aria-pressed={form.otra} onClick={() => { setForm((f) => ({ ...f, otra: true })); }}>
                            Otra
                        </button>
                    </div>
                    {form.otra && (
                        <div className="rv-sv-contador">
                            <span className="rv-sv-contador__caja" role="group" aria-label="Duración en minutos">
                                <button type="button" aria-label="Cinco minutos menos" disabled={form.minutos <= 5} onClick={() => { setForm((f) => ({ ...f, minutos: Math.max(5, f.minutos - 5) })); }}>
                                    <Icono nombre="minus" />
                                </button>
                                <strong>{form.minutos}</strong>
                                <button type="button" aria-label="Cinco minutos más" disabled={form.minutos >= 480} onClick={() => { setForm((f) => ({ ...f, minutos: Math.min(480, f.minutos + 5) })); }}>
                                    <Icono nombre="plus" />
                                </button>
                            </span>
                            <span style={{ fontSize: 14, color: 'var(--rv-fg-2)' }}>{`minutos · ${textoDuracion(form.minutos)}`}</span>
                        </div>
                    )}
                </div>
                <div style={{ maxWidth: 220 }}>
                    <Campo etiqueta={`Precio (${datos.moneda === 'EUR' ? '€' : datos.moneda})`} tipo="number" modo="decimal" valor={form.precio} onChange={pon('precio')} error={errores.precio ?? null} ayuda="0 si es gratis." />
                </div>
                <div role="group" aria-label="Quién lo hace" className="rv-sv-grupo">
                    <span className="rv-sv-grupo__t">Quién lo hace</span>
                    {nuevo ? (
                        <>
                            <div className="rv-sv-gente">
                                {activos.map((p) => {
                                    const on = form.profesionales.includes(p.id);
                                    return (
                                        <button key={p.id} type="button" aria-pressed={on} onClick={() => { setForm((f) => ({ ...f, profesionales: on ? f.profesionales.filter((x) => x !== p.id) : [...f.profesionales, p.id] })); }}>
                                            <Icono nombre={on ? 'check' : 'plus'} />
                                            <span>{p.display_name}</span>
                                        </button>
                                    );
                                })}
                            </div>
                            {form.profesionales.length === 0 && (
                                <span role="alert" className="rv-sv-nota" style={{ color: 'var(--rv-warning-ink)', fontWeight: 600 }}>
                                    <Icono nombre="triangle-alert" />
                                    <span>Sin nadie que lo haga, no se puede pedir.</span>
                                </span>
                            )}
                        </>
                    ) : (
                        <div className="rv-sv-quien">
                            <span>{quienTexto}</span>
                            <Boton tipo="secundario" icono="pencil" movil={movil} onClick={onQuien}>
                                Cambiar
                            </Boton>
                        </div>
                    )}
                </div>
            </fieldset>

            <div className="rv-sv-pliegues">
                <Pliegue icono="eye" titulo="Para el cliente" resumen={`${form.categoria.trim() === '' ? 'Sin categoría' : form.categoria.trim()} · ${form.descripcion.trim() === '' ? 'sin descripción' : 'con descripción'} · ${nombreColor(form.color).toLocaleLowerCase('es')} en la agenda`} abierto={pliegue === 'cliente'} onAlternar={() => { setPliegue((x) => (x === 'cliente' ? null : 'cliente')); }}>
                    <fieldset disabled={!editable} style={{ border: 0, padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
                        <Campo etiqueta="Categoría" valor={form.categoria} onChange={pon('categoria')} max={60} ayuda="Agrupa los servicios en tu web." />
                        <Campo etiqueta="Descripción (opcional)" filas={3} valor={form.descripcion} onChange={pon('descripcion')} max={2000} ayuda="La ve el cliente al elegirlo." />
                        <div className="rv-sv-grupo">
                            <span className="rv-sv-grupo__t">Color en la agenda</span>
                            <div role="radiogroup" aria-label="Color en la agenda" className="rv-sv-colores">
                                {COLORES.map((c) => (
                                    <button key={c.hex} type="button" role="radio" aria-checked={form.color.toLowerCase() === c.hex.toLowerCase()} aria-label={c.nombre} style={{ background: c.hex }} onClick={() => { pon('color')(c.hex); }}>
                                        <Icono nombre="check" />
                                    </button>
                                ))}
                            </div>
                        </div>
                    </fieldset>
                </Pliegue>

                <Pliegue icono="hourglass" titulo="Cuándo se puede pedir cita" resumen={regla.resumen} abierto={pliegue === 'cuando'} onAlternar={() => { setPliegue((x) => (x === 'cuando' ? null : 'cuando')); }}>
                    <div className="rv-sv-frase">{regla.frase}</div>
                    <div style={{ fontSize: 14, fontWeight: 600, marginTop: -8 }}>{regla.mismoDia}</div>
                    <div className="rv-sv-lineas__l" style={{ borderBottom: 0, padding: 0 }}>
                        <span>Cada cuánto empieza una cita</span>
                        <span>{regla.paso}</span>
                    </div>
                    <p className="rv-sv-nota">
                        <Icono nombre="info" />
                        <span>{regla.propia ? 'Este servicio tiene su propia regla.' : 'Es la de todo el negocio.'} Se cambia en «Cuándo se puede reservar».</span>
                    </p>
                    <div>
                        <Boton tipo="secundario" icono="arrow-right" movil={movil} onClick={() => { navegar('/negocio?pantalla=antelacion'); }}>
                            Cambiar la de todo el negocio
                        </Boton>
                    </div>
                </Pliegue>

                <Pliegue
                    icono="globe"
                    titulo="Cómo se pide"
                    resumen={[form.enLinea ? 'En la web y el widget' : 'Solo desde el panel', datos.firma && form.consentimiento !== '' ? 'pide firma' : 'sin firma', politica === null ? 'sin cobro' : politica.toLocaleLowerCase('es')].join(' · ')}
                    abierto={pliegue === 'como'}
                    onAlternar={() => { setPliegue((x) => (x === 'como' ? null : 'como')); }}
                >
                    <fieldset disabled={!editable} style={{ border: 0, padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
                        <Interruptor etiqueta="En la web y el widget" ayuda={form.enLinea ? 'También desde el chat con el asistente.' : 'Solo se da desde el panel.'} valor={form.enLinea} onChange={pon('enLinea')} desactivado={!editable} />
                        <Desplegable
                            etiqueta="Consentimiento que firma el cliente"
                            valor={datos.firma ? form.consentimiento : ''}
                            opciones={[{ valor: '', texto: 'No pide firmar nada' }, ...plantillas.map((p) => ({ valor: p.id, texto: p.name }))]}
                            onChange={pon('consentimiento')}
                            bloqueado={!datos.firma || !editable}
                            ayuda={!datos.firma ? 'Necesita el bloque Firma eIDAS.' : form.consentimiento !== '' ? 'Se le envía al pedir la cita.' : 'Las plantillas están en Consentimientos.'}
                        />
                    </fieldset>
                    <div className="rv-sv-politica">
                        <Icono nombre="credit-card" />
                        <span>
                            <strong>{politica ?? 'Sin política de cobro'}</strong>
                            <small>{politica === null ? 'No se cobra nada al pedir la cita.' : 'Se cobra al pedir la cita, en todos los canales.'}</small>
                        </span>
                        {datos.politicas.length > 0 || politica !== null ? (
                            <Boton tipo="secundario" icono="arrow-right" movil={movil} onClick={() => { navegar('/pagos?apartado=politicas'); }}>
                                Cambiar en Pagos
                            </Boton>
                        ) : null}
                    </div>
                </Pliegue>
            </div>
            {pie}
        </div>
    );
}

function DatosLectura({ datos, servicio }: { datos: DatosServicios; servicio: ServicioCatalogo }) {
    const v = servicio.current_version;
    const regla = reglaDelServicio(servicio.id, datos.reglas);
    const plantilla = v.consent_template_id == null ? null : (datos.plantillas.find((p) => p.id === v.consent_template_id)?.name ?? 'Pide un consentimiento');
    const politica = servicio.policy_id == null ? 'Sin política de cobro' : (datos.politicas.find((p) => p.id === servicio.policy_id)?.name ?? 'Con política de cobro');
    const lineas: [string, string][] = [
        ['Nombre', v.name],
        ['Categoría', v.category ?? 'Sin categoría'],
        ['Duración', textoDuracion(v.base_duration_minutes)],
        ['Precio', dinero(v.base_price)],
        ['Cada cuánto empieza una cita', regla.paso],
        ['Cuándo se puede pedir cita', regla.resumen],
        ['En la web y el widget', servicio.online_bookable ? 'Sí' : 'No, solo en el local'],
        ['Consentimiento', plantilla ?? 'No pide firmar nada'],
        ['Política de cobro', politica],
    ];
    return (
        <div className="rv-sv-lineas">
            {lineas.map(([k, x]) => (
                <div key={k} className="rv-sv-lineas__l">
                    <span>{k}</span>
                    <span>{x}</span>
                </div>
            ))}
        </div>
    );
}

function Pliegue({ icono, titulo, resumen, abierto, onAlternar, children }: { icono: NombreIcono; titulo: string; resumen: string; abierto: boolean; onAlternar: () => void; children: ReactNode }) {
    return (
        <section className="rv-sv-pliegue">
            <button type="button" className="rv-sv-pliegue__cab" aria-expanded={abierto} onClick={onAlternar}>
                <span className="rv-sv-pliegue__baldosa">
                    <Icono nombre={icono} />
                </span>
                <span className="rv-sv-pliegue__texto">
                    <strong>{titulo}</strong>
                    <span>{resumen}</span>
                </span>
                <Icono nombre="chevron-down" className="rv-sv-pliegue__flecha" />
            </button>
            {abierto && <div className="rv-sv-pliegue__cuerpo">{children}</div>}
        </section>
    );
}

// ---- Preguntas ----

function Preguntas({ ops, negocio, movil, datos, servicio, version, editable, aplicar, fallo }: Props & { servicio: ServicioCatalogo; version: string | null; editable: boolean; aplicar: (r: ConVersion<ServicioCatalogo>, texto: string) => void; fallo: (e: unknown) => void }) {
    const [editor, setEditor] = useState<{ f: FormPregunta; original: PreguntaServicio | null; probado: boolean } | null>(null);
    const [ocupado, setOcupado] = useState(false);
    const preguntas = servicio.current_version.questions;
    const pon = (cambio: Partial<FormPregunta>) => { setEditor((e) => (e === null ? e : { ...e, f: { ...e.f, ...cambio } })); };

    const guardar = () => {
        if (editor === null || version === null) {
            return;
        }
        if (!sinErroresPregunta(erroresPregunta(editor.f))) {
            setEditor({ ...editor, probado: true });
            return;
        }
        const campos = camposPregunta(editor.f, editor.original, datos.moneda);
        setOcupado(true);
        (editor.f.id === null ? ops.createServiceQuestion(negocio, servicio.id, { kind: editor.f.tipo, ...campos }, version) : ops.updateServiceQuestion(negocio, servicio.id, editor.f.id, campos, version))
            .then((r) => {
                aplicar(r, `Pregunta guardada. Ya es la versión ${String(r.datos.current_version.number)}.`);
                setEditor(null);
            })
            .catch((e: unknown) => { fallo(e); })
            .finally(() => { setOcupado(false); });
    };
    const retirar = (q: PreguntaServicio) => {
        if (version === null) {
            return;
        }
        setOcupado(true);
        ops.retireServiceQuestion(negocio, servicio.id, q.id, version)
            .then((r) => { aplicar(r, 'Pregunta retirada. Las citas que la respondieron la conservan.'); })
            .catch((e: unknown) => { fallo(e); })
            .finally(() => { setOcupado(false); });
    };

    const e = editor?.probado === true ? erroresPregunta(editor.f) : { opciones: {} as Record<string, string> };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {preguntas.length === 0 && editor === null && (
                <div style={{ padding: '24px 16px', borderRadius: 12, background: 'var(--rv-bone)', textAlign: 'center', fontSize: 14, color: 'var(--rv-fg-2)', lineHeight: 1.5 }}>Este servicio no tiene preguntas: dura y cuesta siempre lo mismo.</div>
            )}
            {preguntas.map((q) => (
                <div key={q.id} className="rv-sv-pregunta">
                    <div className="rv-sv-pregunta__cab">
                        <strong>{q.label}</strong>
                        <span className="rv-sv-pregunta__tipo">
                            <Icono nombre={TIPOS_PREGUNTA[q.kind].icono} />
                            {TIPOS_PREGUNTA[q.kind].texto}
                        </span>
                        <span className="rv-sv-pregunta__tipo">{q.required ? 'Obligatoria' : 'Opcional'}</span>
                    </div>
                    {lineasPregunta(q).map((l, i) => (
                        <div key={i} className="rv-sv-pregunta__linea">
                            <span>{l.texto}</span>
                            <span>{l.cambio}</span>
                        </div>
                    ))}
                    {editable && editor === null && (
                        <div className="rv-sv-pregunta__acciones">
                            <Boton tipo="secundario" icono="pencil" movil={movil} disabled={ocupado || version === null} onClick={() => { setEditor({ f: formPregunta(q), original: q, probado: false }); }}>
                                Cambiar
                            </Boton>
                            <Boton tipo="secundario" rojo icono="archive" movil={movil} disabled={ocupado || version === null} onClick={() => { retirar(q); }}>
                                Retirar
                            </Boton>
                        </div>
                    )}
                </div>
            ))}
            {editor !== null && (
                <div className="rv-sv-editor">
                    <strong>{editor.f.id === null ? 'Nueva pregunta' : 'Cambiar la pregunta'}</strong>
                    {editor.f.id === null && (
                        <div role="radiogroup" aria-label="Tipo de pregunta" className="rv-sv-tipos">
                            {(Object.keys(TIPOS_PREGUNTA) as (keyof typeof TIPOS_PREGUNTA)[]).map((k) => (
                                <button key={k} type="button" role="radio" aria-checked={editor.f.tipo === k} onClick={() => { pon({ tipo: k }); }}>
                                    <span>
                                        <Icono nombre={TIPOS_PREGUNTA[k].icono} />
                                    </span>
                                    {TIPOS_PREGUNTA[k].texto}
                                </button>
                            ))}
                        </div>
                    )}
                    <Campo etiqueta="Pregunta" valor={editor.f.texto} onChange={(x) => { pon({ texto: x }); }} error={e.texto ?? null} max={200} mayusculas="sentences" />
                    <Interruptor etiqueta="Obligatoria" ayuda="Sin responderla no se puede pedir la cita." valor={editor.f.obligatoria} onChange={(x) => { pon({ obligatoria: x }); }} />
                    {(editor.f.tipo === 'single_choice' || editor.f.tipo === 'multiple_choice') && (
                        <div className="rv-sv-grupo">
                            <span className="rv-sv-grupo__t">Respuestas</span>
                            {editor.f.opciones.map((o) => {
                                const cambia = (c: Partial<typeof o>) => { pon({ opciones: editor.f.opciones.map((x) => (x.clave === o.clave ? { ...x, ...c } : x)) }); };
                                return (
                                    <div key={o.clave} className="rv-sv-opcion">
                                        <Campo etiqueta="Respuesta" valor={o.texto} onChange={(x) => { cambia({ texto: x }); }} error={e.opciones[o.clave] ?? null} max={120} />
                                        <Campo etiqueta="Minutos (+/−)" tipo="number" modo="numeric" valor={o.minutos} onChange={(x) => { cambia({ minutos: x }); }} />
                                        <Campo etiqueta="Euros (+/−)" tipo="number" modo="decimal" valor={o.euros} onChange={(x) => { cambia({ euros: x }); }} />
                                        <Boton tipo="secundario" rojo icono="x" movil={movil} disabled={editor.f.opciones.length < 2} onClick={() => { pon({ opciones: editor.f.opciones.filter((x) => x.clave !== o.clave) }); }}>
                                            Quitar
                                        </Boton>
                                    </div>
                                );
                            })}
                            <div>
                                <Boton tipo="secundario" icono="plus" movil={movil} onClick={() => { pon({ opciones: [...editor.f.opciones, { clave: claveNueva(), texto: '', minutos: '0', euros: '0' }] }); }}>
                                    Añadir respuesta
                                </Boton>
                            </div>
                        </div>
                    )}
                    {editor.f.tipo === 'number' && (
                        <>
                            <div className="rv-sv-numeros">
                                <Campo etiqueta="Mínimo" tipo="number" modo="numeric" valor={editor.f.minimo} onChange={(x) => { pon({ minimo: x }); }} />
                                <Campo etiqueta="Máximo" tipo="number" modo="numeric" valor={editor.f.maximo} onChange={(x) => { pon({ maximo: x }); }} error={e.maximo ?? null} />
                                <Campo etiqueta="Minutos por unidad" tipo="number" modo="numeric" valor={editor.f.minutosUnidad} onChange={(x) => { pon({ minutosUnidad: x }); }} />
                                <Campo etiqueta="Euros por unidad" tipo="number" modo="decimal" valor={editor.f.eurosUnidad} onChange={(x) => { pon({ eurosUnidad: x }); }} />
                            </div>
                            <p className="rv-sv-nota">
                                <Icono nombre="calculator" />
                                <span>{ejemploUnidades(editor.f, datos.moneda)}</span>
                            </p>
                        </>
                    )}
                    {editor.f.tipo === 'text' && <Campo etiqueta="Largo máximo de la respuesta" tipo="number" modo="numeric" valor={editor.f.largo} onChange={(x) => { pon({ largo: x }); }} ayuda="Una respuesta de texto no cambia la duración ni el precio." />}
                    <span style={{ fontSize: 13, color: 'var(--rv-fg-3)', lineHeight: 1.5 }}>Al guardar se crea una versión nueva del servicio. Las citas ya hechas conservan sus respuestas, su duración y su precio.</span>
                    <div className="rv-sv-botones">
                        <Boton tipo="secundario" icono="x" movil={movil} onClick={() => { setEditor(null); }}>
                            Cancelar
                        </Boton>
                        <Boton icono="check" movil={movil} ocupado={ocupado} disabled={version === null} onClick={guardar}>
                            Guardar la pregunta
                        </Boton>
                    </div>
                </div>
            )}
            {editable && editor === null && (
                <div>
                    <Boton tipo="secundario" icono="plus" movil={movil} disabled={version === null} onClick={() => { setEditor({ f: formPregunta(null), original: null, probado: false }); }}>
                        Añadir una pregunta
                    </Boton>
                </div>
            )}
        </div>
    );
}

// ---- Versiones ----

function Versiones({ ops, negocio, servicio }: Props & { servicio: ServicioCatalogo }) {
    const [versiones, setVersiones] = useState<VersionServicio[] | null>(null);
    const [error, setError] = useState(false);
    useEffect(() => {
        let vivo = true;
        ops.listServiceVersions(negocio, servicio.id, { limit: 50 })
            .then((r) => {
                if (vivo) {
                    setVersiones(r.data);
                }
            })
            .catch(() => {
                if (vivo) {
                    setError(true);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, servicio.id, servicio.current_version.number]);

    if (error) {
        return <p className="rv-sv-nota">No se han podido cargar las versiones. Cierra la ficha y prueba de nuevo.</p>;
    }
    if (versiones === null) {
        return <p className="rv-sv-nota">Cargando las versiones…</p>;
    }
    return (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
            {versiones.map((v) => {
                const actual = v.number === servicio.current_version.number;
                return (
                    <div key={v.id} className="rv-sv-version">
                        <span className={`rv-sv-version__n${actual ? ' rv-sv-version__n--actual' : ''}`}>{v.number}</span>
                        <span>
                            <strong>{`${v.name} · ${textoDuracion(v.base_duration_minutes)} · ${dinero(v.base_price)}`}</strong>
                            <small>{`${diaMes(v.created_at)}${v.questions.length > 0 ? ` · ${String(v.questions.length)} ${v.questions.length === 1 ? 'pregunta' : 'preguntas'}` : ''}`}</small>
                        </span>
                        {actual && <span className="rv-sv-pregunta__tipo">La de ahora</span>}
                    </div>
                );
            })}
            <p className="rv-sv-nota" style={{ paddingTop: 14 }}>Cada cita guarda la versión con la que se pidió: su duración, su precio y sus respuestas no cambian.</p>
        </div>
    );
}
